import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import { connect } from 'react-redux';
import { fetchSeriesAllocation } from 'reducers/series/actions';
import { series } from 'reducers/series/selectors';
import {
  locked,
  modelHasTemplates,
  modelRemainingQuota,
} from 'reducers/models/selectors';
import {
  fetchModelTemplatesSpecs,
  fetchModelTemplatesWorkingState,
  updateModelWorkingState,
} from 'reducers/models/actions';
import { activeFilters } from 'reducers/templateFilters/selectors';
import { selectedOptions } from 'reducers/search/selectors';
import { setSelectedOptions } from 'reducers/search/actions';
import { fetchDashboard } from 'reducers/dashboard/actions';
import { clearAdvancedFilters } from 'reducers/modelAdvanced/actions';
import {
  currentViewer,
  inCartQuantity,
  isModelActive,
  getPreviousModelWorkingStateDenormalized,
  lockedUntil,
  model,
  modelViewer,
  modelWorkingStateDenormalized,
  orderedModelsBySeriesAndBodyStyles,
  staticDataLoaded,
  workingStateLoaded,
} from 'reducers/models/selectors';
import {
  setModelIsActive,
  setModelIsNotActive,
  setModelLockExpired,
} from 'reducers/models/actions';
import { webShopCloseTime, theme } from 'reducers/dashboard/selectors';
import { modelTemplatesLoading } from 'reducers/loading/selectors';
import { injectIntl } from 'react-intl';
import { addProps, addIntlProps } from 'utils/recompose';
import combineSelectors from 'utils/combineSelectors';
import messages from './messages';
import styles from './styles.module.scss';
import { advancedChartIsSelected } from 'reducers/modelPipeline/selectors';
import { withRouter } from 'react-router-dom';
import { orderDashboardPath } from 'utils/paths';
import {
  checkLockStatus,
  modelLockedRemainingTime,
} from 'reducers/modelsViewer/selectors';
import { resetCheckLockStatus } from 'reducers/modelsViewer/actions';
import { getCurrentUser } from 'utils/localStorage';
import { modelLockRemainingTimeThreshold } from 'constants/values';
import { clearAllDesignOptionFilters } from 'reducers/templateFilters/actions';

export const checkWebShopClosed = (closeTime, currentTime) => {
  if (closeTime) {
    const end = new Date(closeTime + 'Z').getTime();
    const now = currentTime.getTime();
    return Math.sign(end - now) <= 0;
  }
};

const mapStateToProps = combineSelectors({
  activeFilters,
  advancedChartIsSelected,
  checkLockStatus,
  currentViewer,
  getPreviousModelWorkingStateDenormalized,
  inCartQuantity,
  isModelActive,
  locked,
  lockedUntil,
  model,
  modelHasTemplates,
  modelLockedRemainingTime,
  modelRemainingQuota,
  modelTemplatesLoading,
  modelViewer,
  modelWorkingStateDenormalized,
  orderedModelsBySeriesAndBodyStyles,
  selectedOptions,
  series,
  staticDataLoaded,
  theme,
  webShopCloseTime,
  workingStateLoaded,
});

const mapDispatchToProps = {
  clearAllDesignOptionFilters,
  clearAdvancedFilters,
  fetchDashboard,
  fetchModelTemplatesSpecs,
  fetchModelTemplatesWorkingState,
  fetchSeriesAllocation,
  resetCheckLockStatus,
  setModelIsActive,
  setModelIsNotActive,
  setModelLockExpired,
  setSelectedOptions,
  updateModelWorkingState,
};

export const exitModelWorkspace = async ({
  autosave,
  currentRemainingQuota,
  isClosed,
  locked,
  modelId,
  prevProps,
  shouldTriggerAutoSave,
  unmountExitCall,
}) => {
  const prevHasRemainingQuota =
    prevProps && prevProps.currentRemainingQuota > 0;
  const hasRemainingQuota = currentRemainingQuota > 0;
  document.documentElement.scrollTop = 0;

  if (
    !isClosed &&
    prevProps &&
    !prevProps.locked &&
    prevHasRemainingQuota &&
    prevProps.modelId
  ) {
    await autosave(prevProps.modelId);
  }

  if (
    !isClosed &&
    !locked &&
    modelId &&
    unmountExitCall &&
    hasRemainingQuota &&
    shouldTriggerAutoSave
  ) {
    autosave(modelId);
  }
};

export const enterModelWorkspace = async ({
  autosave,
  fetchModelTemplatesSpecs,
  fetchModelTemplatesWorkingState,
  locked,
  modelId,
  setCurrentRemainingQuota,
  setModelIsActive,
  setScrollTop,
  staticDataLoaded,
  that,
}) => {
  window.addEventListener('scroll', setScrollTop);
  that.timer = setTimeout(() => {
    window.addEventListener('scroll', setModelIsActive);
    window.addEventListener('click', setModelIsActive);
  }, 10000);

  if (!staticDataLoaded) {
    fetchModelTemplatesSpecs({ modelId });
  }
  const modelTemplateState = await fetchModelTemplatesWorkingState({ modelId });
  const remainingQuota =
    modelTemplateState.payload.entities.modelState[modelId].remainingQuota;
  setCurrentRemainingQuota(remainingQuota);
  const hasRemainingQuota = remainingQuota > 0;
  !locked && modelId && hasRemainingQuota && autosave(modelId);
};

export const resetModelWorkspace = async ({
  autosave,
  currentRemainingQuota,
  fetchModelTemplatesSpecs,
  fetchModelTemplatesWorkingState,
  isClosed,
  isModelActive,
  locked,
  modelId,
  prevProps,
  setCurrentRemainingQuota,
  setModelIsActive,
  setScrollTop,
  staticDataLoaded,
  that,
}) => {
  await exitModelWorkspace({
    autosave,
    currentRemainingQuota,
    isClosed,
    isModelActive,
    locked,
    modelId,
    prevProps,
  });
  enterModelWorkspace({
    autosave,
    fetchModelTemplatesSpecs,
    fetchModelTemplatesWorkingState,
    locked,
    modelId,
    setCurrentRemainingQuota,
    setModelIsActive,
    setScrollTop,
    staticDataLoaded,
    that,
  });
};

export const autosave = ({
  getPreviousModelWorkingStateDenormalized,
  modelWorkingStateDenormalized,
  updateModelWorkingState,
  webShopCloseTime,
}) => async previousModelId => {
  const currentTime = new Date();
  const isClosed = checkWebShopClosed(webShopCloseTime, currentTime);
  if (!isClosed && !previousModelId) {
    await updateModelWorkingState(modelWorkingStateDenormalized);
  } else if (!isClosed && previousModelId) {
    const previousModelWorkingStateDenormalized = getPreviousModelWorkingStateDenormalized(
      previousModelId,
    );
    await updateModelWorkingState(previousModelWorkingStateDenormalized);
  }
};

export async function componentDidMount() {
  const {
    autosave,
    fetchDashboard,
    fetchModelTemplatesSpecs,
    fetchModelTemplatesWorkingState,
    fetchSeriesAllocation,
    locked,
    modelId,
    setCurrentRemainingQuota,
    setModelIsActive,
    setScrollTop,
  } = this.props;
  fetchDashboard();
  fetchSeriesAllocation();

  enterModelWorkspace({
    autosave,
    fetchModelTemplatesSpecs,
    fetchModelTemplatesWorkingState,
    locked,
    modelId,
    setCurrentRemainingQuota,
    setModelIsActive,
    setScrollTop,
    that: this,
  });
}

const headerStyles = ({ locked }) => !locked && styles.seriesHeader;

const chartStickyStyle = ({ chartSticky, advancedChartIsSelected }) =>
  chartSticky && !advancedChartIsSelected && styles.chartSticky;

export const initialState = {
  checkModelActivity: false,
  chartSticky: false,
  collapsedChart: false,
  hasScrolled: false,
  inReviewView: true,
  isClosed: false,
  scrollTop: null,
  searchView: false,
  currentRemainingQuota: null,
  isWarningOpen: false,
};

const showWarning = () => () => ({ isWarningOpen: true });
const hideWarning = () => () => ({ isWarningOpen: false });

export const setCurrentRemainingQuota = () => quota => ({
  currentRemainingQuota: quota,
});

export const handleShowSearch = ({
  activeFilters,
  showWarning,
  showSearch,
}) => () => {
  if (Object.keys(activeFilters).length) {
    showWarning();
    return;
  }
  showSearch();
};

const handleClearFunctionalGruopFilters = ({
  hideWarning,
  clearAllDesignOptionFilters,
  showSearch,
}) => () => {
  hideWarning();
  clearAllDesignOptionFilters();
  showSearch();
};

export const closeChart = () => () => ({
  collapsedChart: true,
});

export const openChart = () => () => ({
  collapsedChart: false,
});

export const makeChartSticky = () => () => ({
  chartSticky: true,
});

export const resetChartSticky = () => () => ({
  chartSticky: false,
});

export const showSearch = () => () => {
  return { searchView: true };
};

export const closeLocalSearch = () => () => {
  return { searchView: false };
};

export const closeSearch = ({ closeLocalSearch, setSelectedOptions }) => () => {
  setSelectedOptions(null);
  closeLocalSearch();
};

export const showSubmitted = () => () => {
  return { inReviewView: false };
};

export const showInReview = () => () => {
  return { inReviewView: true };
};

export const setIsClosed = () => () => {
  return { isClosed: true };
};

export const setScrolledState = () => () => {
  return { hasScrolled: true };
};

export const selectSubmittedIfWSClosed = ({
  showSubmitted,
  setIsClosed,
  webShopCloseTime,
}) => () => {
  const currentTime = new Date();
  const isClosed = checkWebShopClosed(webShopCloseTime, currentTime);
  if (isClosed) {
    setIsClosed();
    showSubmitted();
  }
};

export const setScrollTop = () => () => ({
  scrollTop: document.documentElement.scrollTop,
});

export const resetScrollTop = () => () => ({ scrollTop: null });

export const lockedBackground = ({ theme }) => ({
  backgroundColor: theme.palette.primary,
});

export async function componentDidUpdate(prevProps) {
  this.props.selectSubmittedIfWSClosed();
  let hash = window.location.hash;
  let hasHash = hash === '' ? false : true;
  const {
    advancedChartIsSelected,
    autosave,
    chartSticky,
    currentRemainingQuota,
    checkLockStatus,
    clearAdvancedFilters,
    closeSearch,
    collapsedChart,
    fetchModelTemplatesSpecs,
    fetchModelTemplatesWorkingState,
    hasScrolled,
    history,
    isClosed,
    isModelActive,
    locked,
    // makeChartSticky,
    modelId,
    modelLockedRemainingTime,
    modelRemainingQuota,
    modelViewer,
    openChart,
    resetChartSticky,
    resetCheckLockStatus,
    scrollTop,
    setCurrentRemainingQuota,
    setCollapseChart,
    setModelIsActive,
    setModelIsNotActive,
    setModelLockExpired,
    setScrolledState,
    staticDataLoaded,
    workingStateLoaded,
  } = this.props;

  if (!isClosed && !locked && checkLockStatus) {
    const currentUserId = getCurrentUser().userId;
    const modelViewerId = modelViewer && modelViewer.currentViewer.userId;
    const isUnderQuota = modelRemainingQuota && modelRemainingQuota !== 0;
    if (
      modelViewerId === currentUserId &&
      modelLockedRemainingTime(modelId) < modelLockRemainingTimeThreshold
    ) {
      if (isModelActive) {
        if (isUnderQuota) {
          autosave();
        }
        setModelIsNotActive();
      } else {
        setModelLockExpired();
        history.push(orderDashboardPath);
      }
    }

    if (prevProps.locked && !locked) {
      autosave();
    }

    console.log(
      'Remaining lock time in milliseconds',
      modelLockedRemainingTime(modelId),
    ); // temporary logging
    resetCheckLockStatus();
  }

  if (prevProps.modelId !== modelId) {
    resetScrollTop();
    closeSearch();
    openChart();
    clearAdvancedFilters();
    resetChartSticky();
    const that = this;
    const timer = this.timer;

    clearTimeout(timer);
    setModelIsNotActive();
    window.removeEventListener('scroll', setScrollTop);
    window.removeEventListener('scroll', setModelIsActive);
    window.removeEventListener('click', setModelIsActive);
    resetModelWorkspace({
      autosave,
      currentRemainingQuota,
      fetchModelTemplatesSpecs,
      fetchModelTemplatesWorkingState,
      isClosed,
      isModelActive,
      locked,
      modelId,
      prevProps,
      setCurrentRemainingQuota,
      setModelIsActive,
      setScrollTop,
      staticDataLoaded,
      that,
    });
  }

  if (
    scrollTop >= 390 &&
    !collapsedChart &&
    !chartSticky &&
    !advancedChartIsSelected
  ) {
    setCollapseChart();
  }

  if (workingStateLoaded && staticDataLoaded && hasHash) {
    let templateId = hash.substr(1);
    let template = document.getElementById(templateId);
    if (template && !hasScrolled) {
      template.scrollIntoView();
      setScrolledState();
    }
  }

  //   if (
  //     prevProps.collapsedChart !== collapsedChart &&
  //     !collapsedChart &&
  //     !advancedChartIsSelected
  //   ) {
  //     makeChartSticky();
  //   }
}

export const setCollapseChart = () => () => {
  return { collapsedChart: true };
};

const modelId = ({ match }) => match.params.modelId;

const seriesId = ({ match }) => match.params.seriesId;

const seriesName = ({ series }) => series && series.name;

export const nextModelId = ({
  orderedModelsBySeriesAndBodyStyles,
  modelId,
}) => {
  let currentModelIndex =
    orderedModelsBySeriesAndBodyStyles &&
    orderedModelsBySeriesAndBodyStyles.indexOf(modelId);
  if (currentModelIndex >= 0) {
    return currentModelIndex === orderedModelsBySeriesAndBodyStyles.length - 1
      ? null
      : orderedModelsBySeriesAndBodyStyles[currentModelIndex + 1];
  }
};

export const previousModelId = ({
  orderedModelsBySeriesAndBodyStyles,
  modelId,
}) => {
  let currentModelIndex =
    orderedModelsBySeriesAndBodyStyles &&
    orderedModelsBySeriesAndBodyStyles.indexOf(modelId);
  if (currentModelIndex >= 0) {
    return currentModelIndex === 0
      ? null
      : orderedModelsBySeriesAndBodyStyles[currentModelIndex - 1];
  }
};

export const showModelTemplateHeader = ({
  modelHasTemplates,
  collapsedChart,
  locked,
}) => modelHasTemplates && !collapsedChart && !locked;

export function componentWillUnmount() {
  const {
    autosave,
    currentRemainingQuota,
    isClosed,
    isModelActive,
    locked,
    modelId,
    modelLockedRemainingTime,
    setModelIsActive,
    setModelIsNotActive,
    setScrollTop,
  } = this.props;

  clearTimeout(this.timer);
  setModelIsNotActive();
  window.removeEventListener('scroll', setScrollTop);
  window.removeEventListener('scroll', setModelIsActive);
  window.removeEventListener('click', setModelIsActive);

  const shouldTriggerAutoSave =
    modelLockedRemainingTime(modelId) > modelLockRemainingTimeThreshold;

  exitModelWorkspace({
    autosave,
    currentRemainingQuota,
    isClosed,
    isModelActive,
    locked,
    modelId,
    shouldTriggerAutoSave,
    unmountExitCall: true,
  });
}

export default compose(
  withRouter,
  injectIntl,
  addIntlProps({
    messages,
    propNames: ['seriesLabel', 'loading', 'noTemplates'],
  }),
  addProps({
    modelId,
    seriesId,
  }),
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(initialState, {
    closeChart,
    closeLocalSearch,
    makeChartSticky,
    openChart,
    resetChartSticky,
    resetScrollTop,
    setCollapseChart,
    setCurrentRemainingQuota,
    setIsClosed,
    setScrolledState,
    setScrollTop,
    showInReview,
    showSearch,
    showSubmitted,
    showWarning,
    hideWarning,
  }),
  withHandlers({
    autosave,
    closeSearch,
    selectSubmittedIfWSClosed,
    handleShowSearch,
    handleClearFunctionalGruopFilters,
  }),
  lifecycle({ componentDidMount, componentDidUpdate, componentWillUnmount }),
  addProps({
    chartStickyStyle,
    headerStyles,
    lockedBackground,
    nextModelId,
    previousModelId,
    seriesName,
  }),
  addProps({
    showModelTemplateHeader,
  }),
);
